import { atom } from 'jotai';
import { atomWithImmer } from 'jotai-immer';
import { atomFamily } from 'jotai/utils'; // <-- Import atomFamily and loadable
import { collection, query, orderBy, limit, getDocs, where, startAfter, CollectionReference, QueryConstraint, getFirestore, QueryDocumentSnapshot, DocumentSnapshot, DocumentData } from 'firebase/firestore/lite';
import { Post, PostStatus } from '@creator/sdk/modules/upvote/upvote.model'; // Assume PostStatus enum exists
import creatorSdk from '@src/services/creator-sdk';
import { getFlairNameById } from '@src/model/token/helpers';
import { jotaiStore } from '../store';
import reduxStore from '@src/store';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';
export type PostCollectionType = 'partner' | 'pinned' | 'user' | 'community' | 'recommended' | 'pending' | 'deleted';

// ----- Global Post Cache -----
export const postsAtom = atomWithImmer<Record<string, Post>>({});
export const getPostAtom = (postId: string) => atom(get => get(postsAtom)[postId]);

// ----- Utility Functions -----
export function setPost(post: Post) {
    jotaiStore.set(postsAtom, draft => {
        draft[post.id] = post;
    });
    reduxStore.getActions().upvote.setPost(post); // Keep for compatibility if needed
}

export function setPosts(posts: Record<string, Post>) {
    jotaiStore.set(postsAtom, draft => {
        Object.assign(draft, posts);
    });
    reduxStore.getActions().upvote.setPosts(posts); // Keep for compatibility if needed
}

export interface PostCollectionCompositeKeyParams {
    type: PostCollectionType;
    tokenName: string;
    flairId?: string;
    userId?: string;
}

// Genreates a unique key for the collection based on the provided parameters, to identify the atom family
export function generatePostCollectionKey(params: PostCollectionCompositeKeyParams) {
    let key = `${params.type}_${params.tokenName}`;
    if (params.flairId) key += `_${params.flairId}`;
    if (params.userId) key += `_${params.userId}`;
    return key;
}

// ----- Define the shape of state for a single collection -----
interface PostCollectionState {
    ids: string[];
    lastDoc: QueryDocumentSnapshot<Post> | null; // Use QueryDocumentSnapshot from getDocs result
    hasMore: boolean;
    status: LoadStatus;
    error: Error | null;
}

// ----- Atom Family for Paginated Post Collections -----
export const postCollectionFamily = atomFamily((params: PostCollectionCompositeKeyParams) => {
    const baseAtom = atomWithImmer<PostCollectionState>({
        ids: [],
        lastDoc: null,
        hasMore: true, // Assume more initially, fetch will confirm
        status: 'idle',
        error: null,
    });
    baseAtom.debugLabel = generatePostCollectionKey(params);
    return baseAtom;
}, (a, b) => generatePostCollectionKey(a) === generatePostCollectionKey(b));

// Define constraints based on parameters
const getQueryConstraints = (params: PostCollectionCompositeKeyParams): QueryConstraint[] => {
    const { type, tokenName, flairId, userId } = params;
    const constraints: QueryConstraint[] = [where('tokenName', '==', tokenName)];

    switch (type) {
        case 'partner':
            constraints.push(
                where('status', '==', PostStatus.PUBLISHED),
                where('createdByWhitelistedUser', '==', true),
                orderBy('createdAt', 'desc')
            );
            if (flairId) constraints.push(where('flairs', 'array-contains', getFlairNameById(flairId, tokenName)));
            break;
        case 'community':
            constraints.push(
                where('status', '==', PostStatus.PUBLISHED),
                where('createdByWhitelistedUser', '==', false),
                orderBy('createdAt', 'desc')
            );
            if (flairId) constraints.push(where('flairs', 'array-contains', getFlairNameById(flairId, tokenName)));
            break;
        case 'pinned':
            constraints.push(
                where('status', '==', PostStatus.PUBLISHED),
                where('isPinned', '==', true),
                orderBy('pinnedTime', 'desc')
            );
            break;
        case 'recommended':
            const lastWeek = new Date();
            lastWeek.setDate(lastWeek.getDate() - 7);
            constraints.push(
                where('status', '==', PostStatus.PUBLISHED),
                where('createdAt', '>=', lastWeek),
                orderBy('totalViews', 'desc') // Note: Firestore requires inequality first for multiple orderBys/wheres
            );
            break;
        case 'user':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(
                where('publisherId', '==', userId),
                where('status', '==', PostStatus.PUBLISHED),
                orderBy('createdAt', 'desc')
            );
            break;
        case 'pending':
            constraints.push(
                where('status', 'in', [PostStatus.PENDING]), // Use Enum/Constant
                orderBy('createdAt', 'desc')
            );
            break;
        case 'deleted':
            constraints.push(
                where('status', 'in', [PostStatus.DELETED_BY_DOMAIN_ADMIN, PostStatus.DELETED_BY_CATEGORY_ADMIN, PostStatus.DELETED_BY_TOKEN_ADMIN]), // Use Enum/Constant
                orderBy('createdAt', 'desc')
            );
            break;
        default:
            // Optional: Add exhaustive check helper
            const _: never = type;
            throw new Error(`Unsupported post type: ${type}`);
    }
    return constraints;
};

export type FetchActionParams = PostCollectionCompositeKeyParams & { fetchLimit?: number };

export const loadMorePostsAtom = atom(
    null, // Write-only atom
    async (get, set, params: FetchActionParams) => {
        const { fetchLimit = 20, ...keyParams } = params;
        const collectionAtom = postCollectionFamily(keyParams);

        const currentState = get(collectionAtom);

        if (currentState.status === 'loading') return;

        // Update status to loading
        set(collectionAtom, draft => {
            draft.status = 'loading';
            draft.error = null; // Clear previous errors
        });

        try {
            const queryConstraints = getQueryConstraints(params);
            const lowerBound = currentState.lastDoc;
            const { ids, postsData, lastDoc, hasMore } = await fetchPosts({ fetchLimit, queryConstraints, lowerBound });

            // Update global post cache
            setPosts(postsData);

            // Update the specific collection's state
            set(collectionAtom, draft => {
                // Use Set to avoid duplicates if reloading the first page or overlaps occur
                const existingIds = new Set(draft.ids);
                ids.forEach(id => existingIds.add(id));
                draft.ids = Array.from(existingIds);

                draft.lastDoc = lastDoc ?? null; // Update lastDoc, null if no docs returned
                draft.hasMore = hasMore;
                draft.status = 'success';
                draft.error = null;
            });

        } catch (error: any) {
            console.error('Error fetching posts:', error);
            // Update status to error
            set(collectionAtom, draft => {
                draft.status = 'error';
                draft.error = error instanceof Error ? error : new Error('An unknown error occurred');
            });
        }
    }
);


export interface FetchPostsPayload {
    lowerBound?: DocumentSnapshot<Post> | null;
    fetchLimit?: number;
    queryConstraints?: QueryConstraint[];
}

export const fetchPosts = async (payload: FetchPostsPayload) => {
    const { lowerBound, fetchLimit = 20, queryConstraints = [] } = payload;

    const db = getFirestore(creatorSdk.getApp());
    const postsRef = collection(db, 'Post') as CollectionReference<Post>;

    const paginationConstraints = lowerBound ? [startAfter(lowerBound)] : [];

    const postsQuery = query(
        postsRef,
        ...queryConstraints, // Base filters/sorting
        ...paginationConstraints, // Pagination
        limit(fetchLimit)
    );

    const snapshot = await getDocs(postsQuery);

    const ids: string[] = [];
    const postsData: Record<string, Post> = {};
    snapshot.forEach(docSnap => {
        const data = docSnap.data();
        postsData[docSnap.id] = { ...data, id: docSnap.id };
        ids.push(docSnap.id);
    });

    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;
    const hasMore = snapshot.size === fetchLimit;

    return { ids, postsData, lastDoc, hasMore };
};

